import { useEffect, useMemo, useRef } from 'react';
import { useIsForegroundNow } from 'shared/src/hooks';
import type { MarketDetail } from 'shared/src/api';
import { useMumulLiveProfileStatus } from '../queries/useMumulLiveProfileStatus';
import { useMumulLiveCurrentCampaign } from '../queries/useMumulLiveCurrentCampaign';
import { useMumulLiveProfilePosts } from '../queries/useMumulLiveProfilePosts';
import { useOnPressMumulLiveProfile } from './useOnPressMumulLiveProfile';
import { useLogLiveProfileBadge } from './useLogLiveProfileBadge';
import { useIsMumulLiveExposed } from './useIsMumulLiveExposed';
import { useIsMumulLiveExpTarget } from './useIsMumulLiveExpTarget';
import { useLogCheckExp1867Target } from './useLogCheckExp1867Target';
import {
  getMumulLiveStatusDisplay,
  type MumulLiveStatusDisplay,
} from '../constants/mumulLiveStatusDisplay';

type UseMarketMumulLiveResult = {
  /** 상태별 프로필 표시 정보. null 이면 노출 없음(DEFAULT/미노출). */
  statusDisplay: MumulLiveStatusDisplay | null;
  /** UNREAD 스토리 여부. 프로필 링 진입 애니메이션 재생 조건. */
  isUnread: boolean;
  /** 프로필 콘텐츠가 1개 이상 있는지 여부. 콘텐츠 더보기 노출 조건. */
  hasContents: boolean;
  /** 가장 최근 콘텐츠의 게시 시각. NEW 뱃지 판별 기준. */
  latestPostedAt: string | null;
  onPressProfile: () => void;
};

/**
 * 마켓홈 무물라이브 프로필 관련 로직 일체(EXP 노출 게이트 / 상태·캠페인 조회 /
 * 노출 로깅 / 클릭 액션)를 캡슐화한다.
 */
export const useMarketMumulLive = (
  market: MarketDetail['market'] | undefined,
): UseMarketMumulLiveResult => {
  const profileSno = market?.mumul_live_profile_sno ?? null;
  const profileName = market?.name ?? '';

  // 노출 대상(EXP1867 B군) 여부. refetch 는 enabled 를 무시하고 강제 호출되므로
  // 비노출(대조군)에서 캐시가 채워지지 않도록 refetch 자체를 게이팅한다.
  const isExposed = useIsMumulLiveExposed();

  const {
    data: mumulLiveProfileStatus,
    refetch: refetchMumulLiveProfileStatus,
  } = useMumulLiveProfileStatus(profileSno);

  // 스토리 등 네이티브 화면 갔다가 돌아올 때(포그라운드 복귀)마다 상태 갱신.
  // 마켓홈은 unmount 되지 않아 staleTime/refetchOnMount 로는 갱신되지 않는다.
  const isForeground = useIsForegroundNow();
  const wasForeground = useRef(isForeground);
  useEffect(() => {
    if (isExposed && isForeground && !wasForeground.current) {
      refetchMumulLiveProfileStatus();
    }
    wasForeground.current = isForeground;
  }, [isExposed, isForeground, refetchMumulLiveProfileStatus]);

  // status 는 대조군(A)에서도 조회되므로 CHECK_EXP_1867_TARGET 로깅에만 쓰고,
  // UI(뱃지/랜딩)에는 노출 대상(B)에서만 값이 있는 exposedStatus 를 쓴다.
  const status = mumulLiveProfileStatus?.profile_status;
  const exposedStatus = isExposed ? status : undefined;
  const isOnair = exposedStatus === 'LIVE';

  // LIVE 노출 시 현재 캠페인 조회 (campaign_sno 로깅/랜딩용)
  const { data: currentCampaign, refetch: refetchCurrentCampaign } =
    useMumulLiveCurrentCampaign(profileSno, isOnair);
  const onairCampaignSno = isOnair
    ? (currentCampaign?.campaign?.sno ?? null)
    : null;

  // 콘텐츠 더보기 노출 조건 — 노출 대상(EXP1867 B군)이면서 콘텐츠가 1개 이상일 때만 노출한다.
  const { data: profilePosts } = useMumulLiveProfilePosts(profileSno);
  const firstPagePosts = useMemo(
    () => profilePosts?.pages[0]?.post_list ?? [],
    [profilePosts],
  );

  // 최신순 응답이지만 정렬을 신뢰하지 않고 최댓값을 취한다.
  const latestPostedAt = useMemo(
    () =>
      firstPagePosts.reduce<string | null>(
        (latest, post) =>
          latest == null || post.posted_at > latest ? post.posted_at : latest,
        null,
      ),
    [firstPagePosts],
  );

  // CHECK_EXP_1867_TARGET: 마켓 프로필 영역 노출 시 대조군(A)·실험군(B) 모두 1회.
  // 프로필이 있는 마켓은 상태·콘텐츠 조회가 끝난 뒤 발화해야 파라미터가 확정된다.
  const isExpTarget = useIsMumulLiveExpTarget();
  const logCheckExp1867Target = useLogCheckExp1867Target();
  const marketSno = market?.sno ?? null;
  const loggedMarketSno = useRef<number | null>(null);
  useEffect(() => {
    if (!isExpTarget || marketSno == null) return;
    if (loggedMarketSno.current === marketSno) return;
    if (profileSno != null && (status == null || profilePosts == null)) return;

    loggedMarketSno.current = marketSno;
    logCheckExp1867Target({
      marketSno,
      profileStatus: status ?? null,
      hasPost: firstPagePosts.length > 0,
    });
  }, [
    isExpTarget,
    marketSno,
    profileSno,
    status,
    profilePosts,
    firstPagePosts,
    logCheckExp1867Target,
  ]);

  const onPressProfile = useOnPressMumulLiveProfile({
    profileSno,
    profileName,
    status: exposedStatus,
    refetchCurrentCampaign,
    refetchStatus: refetchMumulLiveProfileStatus,
  });

  const { logDisplayLiveProfileBadge } = useLogLiveProfileBadge();

  // DISPLAY_LIVE_PROFILE_BADGE: 프로필 뱃지(LIVE/스토리) 노출 시
  // LIVE 는 campaign_sno 가 확보된 뒤 발화, 스토리는 즉시 발화(campaignSno=null)
  useEffect(() => {
    if (
      profileSno == null ||
      exposedStatus == null ||
      exposedStatus === 'DEFAULT'
    )
      return;
    if (exposedStatus === 'LIVE' && onairCampaignSno == null) return;

    logDisplayLiveProfileBadge({
      profileSno,
      profileName,
      status: exposedStatus,
      campaignSno: onairCampaignSno,
    });
  }, [
    profileSno,
    profileName,
    exposedStatus,
    onairCampaignSno,
    logDisplayLiveProfileBadge,
  ]);

  return {
    statusDisplay: getMumulLiveStatusDisplay(exposedStatus),
    isUnread: exposedStatus === 'HAS_UNREAD_STORY',
    hasContents: isExposed && firstPagePosts.length > 0,
    latestPostedAt,
    onPressProfile,
  };
};
